import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { isCycleEvaluationGroupReadOnly } from './cycleEvaluationGroupReadOnly';
import { CYCLE_EVALUATION_GROUP_STEP_COMPONENTS } from './cycleEvaluationGroupStepComponents';
import {
    getCycleEvaluationGroupFurthestStepIndex,
    getCycleEvaluationGroupStepFromPath,
    getCycleEvaluationGroupStepPath,
    getNewCycleEvaluationGroupPath,
    getVisibleCycleEvaluationGroupSteps,
    groupIncludesFeedback360,
    isNewCycleEvaluationGroupRoute,
    resolveCycleEvaluationGroupLandingPath,
    resolveCycleEvaluationGroupResumeStep,
} from './cycleEvaluationGroupSteps';
import { FormNavigator } from '../shared/FormNavigator';
import { RouteName } from '../types';

import type { FormNavigatorRoute } from '../shared/types';
import type { Step } from '@/components/Stepper/StepItem';
import type { CycleEvaluationGroupStepsType } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import {
    useAmelioReviewTemplates,
    useReviewTemplates,
} from '@/api/review-services/review-template';
import {
    cycleInformationNextBlockedAtom,
    cycleInformationOnNextAtom,
    deadlinesOnNextAtom,
    evaluationElementsLiveSelectionAtom,
    evaluationElementsOnNextAtom,
    feedback360SettingsOnNextAtom,
} from '@/atoms/cycleEvaluation';
import { LoaderIcon } from '@/components/OverlayLoader/OverlayLoader';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { CompletedCycleNotice } from '@/pages/CycleEvaluation/components/CompletedCycleNotice/CompletedCycleNotice';
import { findAttachedGroupEvaluation } from '@/pages/CycleEvaluation/EvaluationElements/evaluationSelection';
import {
    getPersistedTemplateId,
    isTemplateStillAvailable,
} from '@/pages/CycleEvaluation/EvaluationElements/templateOptions';

const FEEDBACK_360_TEMPLATE_PAGE_SIZE = 100;

export interface CycleEvaluationGroupFormNavigatorParams {
    step: string;
    cycleEvaluationGroupId: string;
}

const STEP_LABEL_KEYS = {
    [CycleEvaluationGroupSteps.CYCLE_INFORMATION]: 'cycleEvaluationGroupStepper.cycleInformation',
    [CycleEvaluationGroupSteps.EVALUATION_ELEMENTS]:
        'cycleEvaluationGroupStepper.evaluationElements',
    [CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS]:
        'cycleEvaluationGroupStepper.feedback360Settings',
    [CycleEvaluationGroupSteps.DEADLINES]: 'cycleEvaluationGroupStepper.deadlines',
    [CycleEvaluationGroupSteps.COMMUNICATIONS]: 'cycleEvaluationGroupStepper.communications',
} as const;

export const CycleEvaluationGroupFormNavigator = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const { step, cycleEvaluationGroupId } = useParams<CycleEvaluationGroupFormNavigatorParams>();

    const isNewCycle = isNewCycleEvaluationGroupRoute(cycleEvaluationGroupId);

    const {
        data: group,
        isPending,
        isError,
        error,
    } = useGetCycleEvaluationGroup(isNewCycle ? undefined : cycleEvaluationGroupId);

    const cycleInformationOnNext = useAtomValue(cycleInformationOnNextAtom);
    const evaluationElementsOnNext = useAtomValue(evaluationElementsOnNextAtom);
    const feedback360SettingsOnNext = useAtomValue(feedback360SettingsOnNextAtom);
    const deadlinesOnNext = useAtomValue(deadlinesOnNextAtom);

    const onNextCallbacks = useMemo(
        () => ({
            [CycleEvaluationGroupSteps.CYCLE_INFORMATION]: cycleInformationOnNext,
            [CycleEvaluationGroupSteps.EVALUATION_ELEMENTS]: evaluationElementsOnNext,
            [CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS]: feedback360SettingsOnNext,
            [CycleEvaluationGroupSteps.DEADLINES]: deadlinesOnNext,
        }),
        [
            cycleInformationOnNext,
            evaluationElementsOnNext,
            feedback360SettingsOnNext,
            deadlinesOnNext,
        ],
    );

    const isCycleInformationNextBlocked = useAtomValue(cycleInformationNextBlockedAtom);

    const liveSelection = useAtomValue(evaluationElementsLiveSelectionAtom);
    const setLiveSelection = useSetAtom(evaluationElementsLiveSelectionAtom);

    const hasFeedback360 =
        liveSelection?.cycleEvaluationGroupId === cycleEvaluationGroupId
            ? liveSelection.includesFeedback360
            : groupIncludesFeedback360(group);

    const isPersistedFeedback360 = groupIncludesFeedback360(group);
    const feedback360Child = findAttachedGroupEvaluation(group, EvaluationType.EVALUATION_360);

    const feedback360Evaluation = useGetCycleEvaluation(
        isPersistedFeedback360 ? (feedback360Child?.id ?? '') : '',
    );

    const feedback360Templates = useReviewTemplates({
        pageSize: FEEDBACK_360_TEMPLATE_PAGE_SIZE,
        meta: { reviewType: EvaluationType.EVALUATION_360 },
        options: { enabled: isPersistedFeedback360 },
    });

    const amelioFeedback360Templates = useAmelioReviewTemplates(EvaluationType.EVALUATION_360, {
        enabled: isPersistedFeedback360,
    });

    const persistedFeedback360TemplateId = getPersistedTemplateId(
        feedback360Evaluation.data?.cycleQuestionnaire,
    );

    const isPersistedFeedback360TemplateMissing =
        isPersistedFeedback360 &&
        !feedback360Evaluation.isPending &&
        !feedback360Templates.isLoading &&
        !amelioFeedback360Templates.isLoading &&
        !isTemplateStillAvailable(
            persistedFeedback360TemplateId,
            feedback360Templates.data,
            amelioFeedback360Templates.data,
        );

    const isFeedback360TemplateMissing =
        liveSelection?.cycleEvaluationGroupId === cycleEvaluationGroupId
            ? liveSelection.isFeedback360TemplateMissing
            : isPersistedFeedback360TemplateMissing;

    const isFeedback360EvaluatorsComplete =
        isPersistedFeedback360 &&
        !feedback360Evaluation.isPending &&
        feedback360Evaluation.data?.cycleEvaluatorSelection &&
        feedback360Evaluation.data.cycleEvaluatorSelection.suggestedReviewerCount >= 1 &&
        feedback360Evaluation.data.cycleEvaluatorSelection.cycleEvaluatorCriterias.length > 0;

    const isFeedback360Complete =
        isPersistedFeedback360 && !isFeedback360TemplateMissing && isFeedback360EvaluatorsComplete;

    const isFeedback360StepBlocked =
        isFeedback360TemplateMissing ||
        (hasFeedback360 && !isPersistedFeedback360) ||
        (isPersistedFeedback360 &&
            !feedback360Evaluation.isPending &&
            !feedback360SettingsOnNext &&
            !isFeedback360EvaluatorsComplete);

    const nextBlockedByStep = useMemo(
        () => ({
            [CycleEvaluationGroupSteps.CYCLE_INFORMATION]: isCycleInformationNextBlocked,
            [CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS]: isFeedback360StepBlocked,
            [CycleEvaluationGroupSteps.DEADLINES]: hasFeedback360 && !isFeedback360Complete,
        }),
        [
            isCycleInformationNextBlocked,
            isFeedback360StepBlocked,
            hasFeedback360,
            isFeedback360Complete,
        ],
    );

    const visibleSteps = useMemo(
        () => getVisibleCycleEvaluationGroupSteps({ hasFeedback360 }),
        [hasFeedback360],
    );

    const steps: Step[] = useMemo(
        () =>
            visibleSteps.map((stepId) => ({
                id: stepId,
                label: t(STEP_LABEL_KEYS[stepId]),
            })),
        [visibleSteps, t],
    );

    const routes: FormNavigatorRoute[] = useMemo(
        () =>
            visibleSteps.flatMap((stepId) => {
                const component = CYCLE_EVALUATION_GROUP_STEP_COMPONENTS[stepId];

                return component
                    ? [
                          {
                              path: `${RouteName.CycleEvaluationGroupForm}/${getCycleEvaluationGroupStepPath(stepId)}/:cycleEvaluationGroupId`,
                              component,
                          },
                      ]
                    : [];
            }),
        [visibleSteps],
    );

    // Read lazily at render time — a module-scope read triggers a circular import.
    const renderableSteps = useMemo(
        () =>
            Object.keys(CYCLE_EVALUATION_GROUP_STEP_COMPONENTS) as CycleEvaluationGroupStepsType[],
        [],
    );

    // Cleared when the flow itself is left, not when the elements step unmounts: the cycle read
    // that takes over is still refetching then, and a rail that briefly believes 360 feedback is
    // gone redirects away from the step the save just unlocked.
    useEffect(() => () => setLiveSelection(null), [setLiveSelection]);

    useEffect(() => {
        if (isError) {
            handleError({ error });
        }
    }, [isError, error, handleError]);

    const handleLastStepReached = () => {
        navigate({ path: RouteName.CycleEvaluation });
    };

    const getStepAriaLabel = (ariaStep: Step, index: number) =>
        t('cycleEvaluationGroupStepper.stepAriaLabel', {
            rank: index + 1,
            total: steps.length,
            label: ariaStep.label,
        });

    const getStepRankLabel = (index: number) =>
        t('cycleEvaluationGroupStepper.stepRankLabel', { rank: index + 1 });

    if (!isNewCycle && isPending) {
        return (
            <div className={'flex min-h-screen w-full items-center justify-center'}>
                <LoaderIcon />
            </div>
        );
    }

    if (!isNewCycle && (isError || !group)) {
        return <Redirect to={RouteName.CycleEvaluation} />;
    }

    const requestedStep = getCycleEvaluationGroupStepFromPath(step);

    if (isNewCycle && requestedStep !== CycleEvaluationGroupSteps.CYCLE_INFORMATION) {
        return <Redirect to={getNewCycleEvaluationGroupPath(RouteName.CycleEvaluationGroupForm)} />;
    }

    const isReadOnly = isCycleEvaluationGroupReadOnly(group);

    if (
        !requestedStep ||
        !visibleSteps.includes(requestedStep) ||
        !renderableSteps.includes(requestedStep)
    ) {
        if (group && isReadOnly) {
            return <Redirect to={resolveCycleEvaluationGroupLandingPath({ group })} />;
        }

        const resumeStep = resolveCycleEvaluationGroupResumeStep({
            persistedStep: requestedStep ?? group?.currentStep,
            hasFeedback360,
            renderableSteps,
            isFeedback360Complete,
        });

        return (
            <Redirect
                to={`${RouteName.CycleEvaluationGroupForm}/${getCycleEvaluationGroupStepPath(resumeStep)}/${cycleEvaluationGroupId}`}
            />
        );
    }

    return (
        <FormNavigator
            steps={steps}
            routePrefix={RouteName.CycleEvaluationGroupForm}
            urlParamName={'cycleEvaluationGroupId'}
            getStepFromPath={getCycleEvaluationGroupStepFromPath}
            getStepPath={getCycleEvaluationGroupStepPath}
            backRoute={RouteName.CycleEvaluation}
            onSuccess={handleLastStepReached}
            onNextCallbacks={onNextCallbacks}
            nextBlockedByStep={nextBlockedByStep}
            stepperDataTestId={'cycle-evaluation-group-stepper'}
            routes={routes}
            initialMaxStep={getCycleEvaluationGroupFurthestStepIndex({
                group,
                visibleSteps,
                renderableSteps,
                isFeedback360Complete,
                hasFeedback360,
            })}
            getStepAriaLabel={getStepAriaLabel}
            stepperVariant={'rail'}
            getStepRankLabel={getStepRankLabel}
            readOnlyNotice={isReadOnly ? <CompletedCycleNotice /> : undefined}
            pageTitle={
                group
                    ? t('cycleEvaluationGroupStepper.pageTitle', { name: group.name })
                    : t('cycleEvaluationGroupStepper.pageTitleNew')
            }
            pageSubtitle={t('cycleEvaluationGroupStepper.pageSubtitle')}
            stepsHostingFooter={[
                CycleEvaluationGroupSteps.CYCLE_INFORMATION,
                CycleEvaluationGroupSteps.EVALUATION_ELEMENTS,
                CycleEvaluationGroupSteps.FEEDBACK_360_SETTINGS,
                CycleEvaluationGroupSteps.DEADLINES,
                CycleEvaluationGroupSteps.COMMUNICATIONS,
            ]}
        />
    );
};
